import * as React from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';

import FlexContainer from 'global_elements/Layouts/FlexContainer';
import InlineText from 'global_elements/Text/InlineText';
import { ReactComponent as SortIcon } from 'icons/mho-sort-icon-white.svg';

import { FacilityUserAccountsTableProps, FacilityUserDataRow } from 'types/tableProps';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';

import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Utilities from 'api/lib/Utilities';
import ExcelJS from 'exceljs';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import { saveAs } from 'file-saver';
import { HoverTableStyles } from '../styles';

const isMobile = window.innerWidth <= 1024;

const NoDataComponent: React.JSX.Element = <InlineText text="No Results Found." fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} />;

const exportWorklistToExcel = (accountData: FacilityUserDataRow[]): void => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');

  worksheet.columns = [
    { header: 'AccountID', key: 'accountID', width: 10 },
    { header: 'Name', key: 'name', width: 32 },
    { header: 'Email', key: 'email', width: 32 },
    { header: 'Corporation', key: 'corporation', width: 32 },
    { header: 'Facility', key: 'facility', width: 32 },
    { header: 'Role', key: 'role', width: 32 },
    { header: 'Status', key: 'status', width: 15 },
  ];

  for (let i = 0; i < accountData.length; i += 1) {
    worksheet.addRow({
      accountID: accountData[i].accountID,
      name: accountData[i].name,
      email: accountData[i].email,
      corporation: accountData[i].corporation,
      facility: accountData[i].facility,
      role: accountData[i].role,
      status: accountData[i].status,
    });
  }

  const row = worksheet.getRow(1);
  row.font = { bold: true };

  workbook.xlsx
    .writeBuffer()
    .then((buffer) => saveAs(new Blob([buffer]), `Facility_User_Accounts_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`))
    .catch((err) => console.log('Error writing excel export', err));
};

const columns = (onChangeAccessType: (row: FacilityUserDataRow, e: React.MouseEvent<Element, MouseEvent>) => any, isCoreMeasuresEnabled: boolean): TableColumn<FacilityUserDataRow>[] => {
  const cols: TableColumn<FacilityUserDataRow>[] = [
    {
      name: 'Name',
      selector: (row) => row.name.toLowerCase(),
      format: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: 'Corporation',
      selector: (row) => row.corporation,
      sortable: true,
      maxWidth: isMobile ? '125px' : '150px',
    },
    {
      name: 'Facility',
      selector: (row) => row.facility,
      sortable: true,
      maxWidth: isMobile ? '200px' : '300px',
    },
    {
      name: 'Role',
      selector: (row) => row.role,
      sortable: true,
      maxWidth: '166px',
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      maxWidth: isMobile ? '70px' : '100px',
    },
  ];
  if (isCoreMeasuresEnabled) {
    cols.push({
      name: 'Access',
      selector: (row) => row.accessType,
      sortable: true,
      // eslint-disable-next-line react/jsx-one-expression-per-line
      cell: (row) => (
        <span className="access-type-label">
          {row.accessType}
          {row.status && row.status !== 'Inactive' && <FontAwesomeIcon icon={faPencil} style={{ paddingLeft: '5px' }} onClick={(e) => onChangeAccessType(row, e)} />}
        </span>
      ),
    });
  }
  return cols;
};

const FacilityUserAccountsTable = (props: FacilityUserAccountsTableProps): React.JSX.Element => (
  <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.END} extraClasses="facility-user-account-table">
    <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.END} extraClasses="table-button-container">
      <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER}>
        <InlineText
          text="Search is limited to first 200 results. Please filter your search accordingly."
          fontColor={FontColors.HIGH_PRIORITY}
          fontSize={FontSizes.REGULAR}
          bold
          extraClasses={props.data.length < 200 ? 'invisible' : ''}
        />
      </FlexContainer>

      <Button variant={ButtonVariant.PRIMARY} onClick={() => exportWorklistToExcel(props.data)} disabled={!props.data} extraClasses="no-print">
        <InlineText text="Export to Excel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
      </Button>
    </FlexContainer>
    <DataTable
      dense
      highlightOnHover
      persistTableHead
      pagination
      paginationDefaultPage={props.defaultPage}
      sortIcon={<SortIcon />}
      customStyles={HoverTableStyles}
      columns={columns(props.onChangeAccessType, props.isCoreMeasuresEnabled)}
      data={props.data}
      noDataComponent={NoDataComponent}
      onRowClicked={props.onRowClicked}
      sortFunction={Utilities.caseInsensitiveSort}
      onSort={Utilities.setCellTitles}
      onChangePage={props.onChangePage}
    />
  </FlexContainer>
);

export default FacilityUserAccountsTable;
